import React, { useEffect, useState } from 'react';
import { Loader2, DollarSign, TrendingUp, TrendingDown, Landmark, PieChart as PieIcon } from 'lucide-react';
import { reportDashboard, getBooksTenant } from './api';

const fmtMoney = (n, cur) => {
  const sym = { GBP: '£', INR: '₹', MYR: 'RM ', KZT: '₸', USD: '$', EUR: '€' }[cur] || '';
  const v = Number(n || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  return `${sym}${v}`;
};

const DashboardPage = () => {
  const [k, setK] = useState(null);
  const [loading, setLoading] = useState(true);
  const tenant = getBooksTenant();
  const cur = tenant?.base_currency;

  useEffect(() => {
    reportDashboard().then(setK).catch(() => setK(null)).finally(() => setLoading(false));
  }, []);

  if (loading) return <div className="p-12 text-center text-[#0b1a2e]/50"><Loader2 className="w-6 h-6 animate-spin inline mr-2" /> Loading…</div>;
  if (!k) return <div className="p-12 text-center text-red-700">Failed to load dashboard.</div>;

  const maxTrend = Math.max(...(k.monthly_cash_trend || []).map((m) => Math.abs(m.net_change || 0)), 1);

  return (
    <div>
      <div className="text-xs font-semibold tracking-[0.3em] text-[#0f5c3d] uppercase">MIS</div>
      <h1 className="text-4xl sm:text-5xl text-[#0b1a2e]" style={{ fontFamily: 'Cormorant Garamond, serif', fontWeight: 500 }}>Dashboard</h1>

      <div className="mt-6 grid grid-cols-2 lg:grid-cols-4 gap-3">
        <KPI icon={Landmark} label="Cash on hand" value={fmtMoney(k.cash_balance, cur)} color="bg-[#0f5c3d]" />
        <KPI icon={TrendingUp} label="YTD Revenue" value={fmtMoney(k.ytd_revenue, cur)} color="bg-[#0b1a2e]" />
        <KPI icon={TrendingDown} label="YTD Expense" value={fmtMoney(k.ytd_expense, cur)} color="bg-orange-500" />
        <KPI icon={DollarSign} label="YTD Profit" value={fmtMoney(k.ytd_profit, cur)} color={k.ytd_profit >= 0 ? 'bg-emerald-600' : 'bg-red-600'} />
      </div>

      <div className="mt-6 grid grid-cols-1 lg:grid-cols-3 gap-3">
        <div className="lg:col-span-2 bg-white rounded-2xl border border-black/5 p-5">
          <div className="flex items-center gap-2 mb-4">
            <PieIcon className="w-4 h-4 text-[#0f5c3d]" />
            <div className="text-xs font-semibold uppercase tracking-widest text-[#0b1a2e]">Cash movement — last 6 months</div>
          </div>
          <div className="flex items-end gap-2 h-40" data-testid="cash-trend-chart">
            {(k.monthly_cash_trend || []).map((m) => {
              const pct = Math.round(Math.abs(m.net_change || 0) / maxTrend * 100);
              const positive = (m.net_change || 0) >= 0;
              return (
                <div key={m.month} className="flex-1 flex flex-col items-center gap-1">
                  <div className={`w-full rounded-t ${positive ? 'bg-[#0f5c3d]' : 'bg-red-500'}`} style={{ height: `${pct}%`, minHeight: 2 }} />
                  <div className="text-[10px] font-mono text-[#0b1a2e]/60">{m.month.slice(5)}</div>
                </div>
              );
            })}
          </div>
        </div>
        <div className="bg-white rounded-2xl border border-black/5 p-5">
          <div className="text-xs font-semibold uppercase tracking-widest text-[#0b1a2e] mb-4">Ledger snapshot</div>
          <Stat k="Chart of Accounts" v={k.accounts_count} />
          <Stat k="Posted journals" v={k.journals_count} />
          <Stat k="Base currency" v={tenant?.base_currency} />
          <Stat k="Country" v={tenant?.country_code} />
        </div>
      </div>
    </div>
  );
};

const KPI = ({ icon: Icon, label, value, color }) => (
  <div className="bg-white rounded-2xl border border-black/5 p-4 flex items-start gap-3">
    <div className={`w-9 h-9 rounded-lg ${color} text-white flex items-center justify-center flex-shrink-0`}>
      <Icon className="w-4 h-4" />
    </div>
    <div className="min-w-0">
      <div className="text-[10px] font-semibold uppercase tracking-widest text-[#0b1a2e]/60">{label}</div>
      <div className="text-lg font-medium text-[#0b1a2e] truncate">{value}</div>
    </div>
  </div>
);

const Stat = ({ k, v }) => (
  <div className="flex justify-between py-1.5 border-t border-slate-100 first:border-t-0">
    <span className="text-xs text-[#0b1a2e]/60">{k}</span>
    <span className="text-sm font-semibold text-[#0b1a2e]">{v ?? '—'}</span>
  </div>
);

export default DashboardPage;
